/**
 * `map`, drawn by 2GIS MapGL — the same card, with tiles under it.
 *
 * In PREVIEW the card frame, the heading and the «Открыть в 2ГИС» footer are
 * `MapSketch`'s verbatim, because a visitor switching between a browser with
 * WebGL and one without must not see two different cards. In NAV the chrome
 * comes off: the map is the screen, and everything drawn over it belongs to
 * `NavOverlay`.
 *
 * **This component does not own the map.** One instance lives in `mapHost` for
 * the whole page and merely moves into the slot `MapViewport` renders — which
 * is what lets the same WebGL canvas travel from the board's side card to the
 * full screen without re-downloading a tile (design §1.4, §1.6).
 *
 * **Everything the map DRAWS is drawn here**, including the navigation-mode
 * pieces: the split route, the visitor's own marker, and the camera. The
 * overlay is DOM over a canvas and never touches the map — one writer, so
 * «who moved the camera» has one answer.
 *
 * The `key` never appears in this file: `mapHost` reads it, and the deep link
 * in the footer is keyless by construction.
 */

import { useEffect, useMemo, useRef } from 'react';

import type { MapCardProps } from '../generated/schemas';
import { fitToRoute, follow, CARD_PADDING } from '../../map/camera';
import { mountLocateControl } from '../../map/locateControl';
import { acquireMap } from '../../map/mapHost';
import MapViewport from '../../map/MapViewport';
import {
  clearDashed,
  clearMarkers,
  clearRoute,
  EMPTY_ROUTE_LINES,
  escapeHtml,
  pointsToLngLat,
  syncDashed,
  syncMarkers,
  syncRoute,
  toLngLat,
  type DashedBag,
  type LngLat,
  type MarkerBag,
  type PinModel,
  type RouteLines,
} from '../../map/objects';
import { focusBoardCard } from '../../board/focus';
import { splitRoute } from '../../nav/geo';
import { useNav } from '../../nav/navStore';
import { routeModel, stairwaySegments, totalsLabel } from '../../nav/track';
import type { HtmlMarker } from '@2gis/mapgl/types';

/** The card's zoom when the walk is a single pin and there is no box to fit. */
const LONE_PIN_ZOOM = 16;
/** Following someone on foot: close enough to read a path, wide enough to plan. */
const FOLLOW_ZOOM = 17;
/** `Polyline` has no setters, so the cut point rebuilds two lines. Not per fix. */
const ROUTE_REDRAW_MS = 2000;
/** Room for the bottom sheet under the walk, so the route is not behind it. */
const NAV_PADDING = { top: 88, right: 24, bottom: 220, left: 24 };

export default function MapLive({ props }: { props: MapCardProps }) {
  const markers = useRef<MarkerBag>(new Map());
  const lines = useRef<RouteLines>(EMPTY_ROUTE_LINES());
  const dashed = useRef<DashedBag>(new Map());
  const me = useRef<HtmlMarker | null>(null);

  const nav = props.mode === 'nav';
  const pins = props.pins as PinModel[];
  const center = toLngLat(props.center);
  const routePoints = useMemo(() => pointsToLngLat(props.route?.points), [props.route]);

  // Only the navigation mode subscribes to these, and only because the two
  // things they move — the cut point and the visitor's marker — are the two
  // things on the map that are not the board's.
  const snap = useNav((state) => (nav ? state.track.snap : null));
  const last = useNav((state) => (nav ? state.last : null));
  const following = useNav((state) => state.following);
  const centreOn = useNav((state) => state.centreOn);

  /**
   * What the map has to be re-told. The board rebuilds `props` on every frame
   * (zod returns a fresh object each time), so an effect keyed on the props
   * themselves would re-sync on turns that changed nothing.
   */
  const signature = useMemo(
    () =>
      JSON.stringify([
        pins.map((pin) => [pin.id, pin.lat, pin.lon, pin.n ?? null, pin.kind, pin.label]),
        routePoints,
        // The dashed overlay is built from the legs, so a card whose legs
        // changed has to re-sync even when the drawn line did not.
        (props.legs ?? []).map((leg) => [leg.n_from, leg.n_to, leg.first ?? null, leg.last ?? null, leg.style ?? null]),
        nav,
      ]),
    [pins, routePoints, props.legs, nav],
  );

  // ── the pins, and the frame around the walk ──────────────────────────────
  useEffect(() => {
    let live = true;
    // `acquireMap` is the memoised promise, not a construction: the FIRST sync
    // has to wait for the SDK, and every later one resolves immediately.
    void acquireMap({ center, zoom: props.zoom ?? LONE_PIN_ZOOM }).then((handle) => {
      if (!live || !handle) return;
      markers.current = syncMarkers(handle.map, handle.sdk, markers.current, pins, (pin) => {
        // `Pin.ref` names the card the guide has ALREADY put on the board and
        // described. A tap scrolls to it — no run, because spending one to be
        // told what is already on screen is the worst trade this surface has.
        if (pin.ref) focusBoardCard(pin.ref);
      });
      // The walk is the pins plus whatever the drawn line adds to their box.
      const framed: LngLat[] = [...pins.map(toLngLat), ...routePoints];
      fitToRoute(handle.map, framed, nav ? NAV_PADDING : CARD_PADDING, {
        maxZoom: LONE_PIN_ZOOM,
      });
    });
    return () => {
      live = false;
    };
    // `signature` is the real dependency; `pins`/`routePoints` are derived from
    // the same props and change identity without changing meaning.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature]);

  // ── the route line, cut at the visitor in nav mode ───────────────────────
  useEffect(() => {
    let live = true;
    void acquireMap({ center, zoom: props.zoom ?? LONE_PIN_ZOOM }).then((handle) => {
      if (!live || !handle) return;
      const { walked, ahead } = nav
        ? splitRoute(routePoints, snap)
        : { walked: [] as LngLat[], ahead: routePoints };
      lines.current = syncRoute(handle.map, handle.sdk, lines.current, ahead, walked, {
        minIntervalMs: nav ? ROUTE_REDRAW_MS : 0,
      });
    });
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature, snap]);

  /**
   * The stairways, dashed, above the walk.
   *
   * Drawn in BOTH modes: a flight of steps between two stops is as much a fact
   * about the card as it is about the guidance, and a visitor with a pushchair
   * deciding whether to go at all is reading the card, not the guidance.
   */
  useEffect(() => {
    let live = true;
    void acquireMap({ center, zoom: props.zoom ?? LONE_PIN_ZOOM }).then((handle) => {
      if (!live || !handle) return;
      const route = routeModel(props);
      const segments = route ? stairwaySegments(route) : [];
      dashed.current = syncDashed(handle.map, handle.sdk, dashed.current, segments);
    });
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature]);

  // ── «я здесь», and the camera that follows it ────────────────────────────
  useEffect(() => {
    if (!nav) return;
    let live = true;
    void acquireMap({ center, zoom: props.zoom ?? LONE_PIN_ZOOM }).then((handle) => {
      if (!live || !handle) return;
      if (!last) return;
      const at: LngLat = [last.lng, last.lat];
      const html = meHtml(last.headingDeg, last.accuracyM);
      if (me.current) {
        me.current.setCoordinates(at);
        me.current.setContent(html);
      } else {
        me.current = new handle.sdk.HtmlMarker(handle.map, {
          coordinates: at,
          html,
          anchor: [18, 18],
          zIndex: 40,
          preventMapInteractions: false,
        });
      }
      if (following) follow(handle.map, at, FOLLOW_ZOOM);
    });
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nav, last, following]);

  /** «Покажи эту точку» from the overlay — the map's one writer honours it. */
  useEffect(() => {
    if (!centreOn) return;
    let live = true;
    void acquireMap({ center, zoom: props.zoom ?? LONE_PIN_ZOOM }).then((handle) => {
      if (!live || !handle) return;
      const at = useNav.getState().takeCentre();
      if (at) follow(handle.map, at);
    });
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [centreOn]);

  /**
   * A manual gesture turns following off.
   *
   * `MapEvent.isUser` is what makes this exact rather than a guess: without it
   * the only way to tell our own `setCenter` from a drag is to compare
   * timestamps, and that works about half the time. «Я здесь» turns it back on.
   */
  useEffect(() => {
    if (!nav) return;
    let live = true;
    let detach: (() => void) | null = null;
    void acquireMap({ center, zoom: props.zoom ?? LONE_PIN_ZOOM }).then((handle) => {
      if (!live || !handle) return;
      const onMove = (event: { isUser: boolean }) => {
        if (event.isUser) useNav.getState().setFollowing(false);
      };
      handle.map.on('movestart', onMove);
      detach = () => handle.map.off('movestart', onMove);
    });
    return () => {
      live = false;
      detach?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nav]);

  // «Я здесь» (INV-PH-140): outside the navigation, where the overlay has its own and the camera follows
  // the walker. A tap draws the dot and the accuracy circle and moves the camera; nothing is sent anywhere.
  useEffect(() => {
    if (nav) return;
    let live = true;
    let remove: (() => void) | null = null;
    void acquireMap({ center, zoom: props.zoom ?? LONE_PIN_ZOOM }).then((handle) => {
      if (!live || !handle) return;
      remove = mountLocateControl(handle.sdk, handle.map);
    });
    return () => {
      live = false;
      remove?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nav]);

  // The card leaving the board takes its drawings with it. The MAP survives —
  // it belongs to the page, not to this card.
  useEffect(
    () => () => {
      markers.current = clearMarkers(markers.current);
      lines.current = clearRoute(lines.current);
      dashed.current = clearDashed(dashed.current);
      me.current?.destroy();
      me.current = null;
    },
    [],
  );

  // «2,1 км · ~36 мин» — the walk as a whole. Exact only when EVERY leg was
  // measured by the router (`totalsLabel`).
  const totals = totalsLabel(props);

  const viewport = (
    <MapViewport
      init={{ center, zoom: props.zoom ?? LONE_PIN_ZOOM }}
      className={nav ? 'map-live map-live--nav' : 'map-live'}
    />
  );

  // In nav mode the map IS the screen: no card, no heading, no footer. The
  // deep link moves to the overlay, where it sits with the other controls.
  if (nav) return viewport;

  return (
    <article className="card card--map">
      <header className="card__head">
        <h3 className="card__title">На карте</h3>
        <span className="card__meta">
          {pins.length} {pins.length === 1 ? 'точка' : 'точек'}
        </span>
      </header>
      {viewport}
      {totals ? <p className="map-live__totals">{totals}</p> : null}
      {props.dgis_url ? (
        <footer className="card__actions">
          <a
            className="chip chip--link"
            href={props.dgis_url}
            target="_blank"
            rel="noreferrer noopener"
          >
            Открыть в 2ГИС
          </a>
        </footer>
      ) : null}
    </article>
  );
}

/**
 * The visitor's own marker.
 *
 * A HEADING is only reported in a browser once somebody is actually moving, so
 * with none we draw a plain dot rather than an arrow pointing somewhere
 * invented. The accuracy ring is drawn from the reading, not from a constant:
 * a 40 m circle is the honest picture of a 40 m fix.
 */
function meHtml(headingDeg: number | null, accuracyM: number): string {
  const ring = Math.max(36, Math.min(160, Math.round(accuracyM * 2)));
  const beak =
    headingDeg === null
      ? ''
      : `<span class="nav-me__beak" style="transform:rotate(${escapeHtml(
          String(Math.round(headingDeg)),
        )}deg)"></span>`;
  return (
    `<span class="nav-me" style="--nav-me-ring:${ring}px">` +
    `<span class="nav-me__ring"></span>${beak}<span class="nav-me__dot"></span></span>`
  );
}
